import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import styles from "./InstructorDetails.module.css";

export default function InstructorDetails() {
  const [instructor, setInstructor] = useState(null);
  const [activeTab, setActiveTab] = useState("description");
  const { id } = useParams();
  const [t, i18n] = useTranslation("global");
  
  useEffect(()=>{
    window.scrollTo(0,0);
  });

  useEffect(() => {
    const fetchInstructorDetails = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/instructor/${id}`,
          {
            headers: {
              "Accept-Language": i18n.language,
            },
          }
        );

        const data = await response.json();
        if (data.type === "success") {
          setInstructor(data.data);
        } else {
          console.error("Error fetching instructor details:", data.message);
        }
      } catch (error) {
        console.error("Error fetching instructor details:", error);
      }
    };

    fetchInstructorDetails();
  }, [id, i18n.language]);

  const handleTabClick = (tab) => {
    setActiveTab(tab);
  };

  const handleCourseClick = (course) => {
    // Navigate to course details page
    window.location.href = `/CourseDetails/${course.id}`;
  };

  const tabContent = {
    description: instructor?.description || t("loading"),
  };

  if (!instructor) {
    return <div>{t("loading")}</div>;
  }

  return (
    <>
      <div className={styles.customSection}>
        <div className="container">
          <h1 className="m-0">{t("instructorsPage2.details")}</h1>
          <h6 className="m-0">
            <Link className={styles.linkHover} to="/">{t("instructorsPage2.home")}</Link>
            <span> -</span>
            <Link className={styles.linkHover} to="/Instructors">{t("instructorsPage2.title")}</Link>
            <span> - </span>
            <span className={styles.secondColor}>{t("instructorsPage2.details")}</span>
          </h6>
        </div>
      </div>

      <section className="my-3">
        <div className={`course-container ${styles.instructorDetailsContainer}`}>

          <div className={styles.courseImage}>
            <img
              src={instructor.image}
              className={styles.instructorImage}
              alt={instructor.name}
            />
            <h1 className={styles.instructorName}>
              {instructor.name}
            </h1>
          </div>

          {/* Instructor Overview - Always visible */}
          <h2 className={styles.overviewTitle}>{t("instructorsPage2.overview")}</h2>
          <p className={styles.positionText}>{t("instructorsPage2.position")} : {instructor.position}</p>
          
          <div className={styles.socialLinks}>
            {instructor?.facebook && (
              <a href={instructor.facebook} className={`${styles.socialLink} ${styles.facebook}`} aria-label="Facebook">
                <i className="fab fa-facebook-f"></i>
              </a>
            )}

            {instructor?.twitter && (
              <a href={instructor.twitter} className={`${styles.socialLink} ${styles.twitter}`} aria-label="X (Twitter)">
                <i className="fab fa-x-twitter"></i>
              </a>
            )}

            {instructor?.youtube && (
              <a href={instructor.youtube} className={`${styles.socialLink} ${styles.youtube}`} aria-label="YouTube">
                <i className="fab fa-youtube"></i>
              </a>
            )}

            {instructor?.tiktok && (
              <a href={instructor.tiktok} className={`${styles.socialLink} ${styles.tiktok}`} aria-label="TikTok">
                <i className="fab fa-tiktok"></i>
              </a>
            )}

            {instructor?.whatsapp && (
              <a href={instructor.whatsapp} className={`${styles.socialLink} ${styles.whatsapp}`} aria-label="WhatsApp">
                <i className="fab fa-whatsapp"></i>
              </a>
            )}

            {instructor?.telegram && (
              <a href={instructor.telegram} className={`${styles.socialLink} ${styles.telegram}`} aria-label="Telegram">
                <i className="fab fa-telegram-plane"></i>
              </a>
            )}

            {instructor?.snapchat && (
              <a href={instructor.snapchat} className={`${styles.socialLink} ${styles.snapchat}`} aria-label="Snapchat">
                <i className="fab fa-snapchat-ghost"></i>
              </a>
            )}

            {instructor?.instagram && (
              <a href={instructor.instagram} className={`${styles.socialLink} ${styles.instagram}`} aria-label="Instagram">
                <i className="fab fa-instagram"></i>
              </a>
            )}
          </div>

          {/* Tabs */}
          <ul className={styles.courseTabs}>
            <li
              className={`${styles.tabItem} ${activeTab === "description" ? styles.active : ""}`}
              onClick={() => handleTabClick("description")}
            >
              {t("instructorsPage2.description")}
            </li>
          </ul>

          {/* Tab Content */}
          <div className={styles.courseContent}>
            <p>{tabContent[activeTab]}</p>
          </div>

          {/* Courses Section */}
          <div className={styles.coursesSection}>
            <h3 className={styles.coursesTitle}>{t("instructorsPage2.subjectsTitle")}</h3>
            
            {instructor.courses && instructor.courses.length > 0 ? (
              <div className="row mt-3">
                {instructor.courses.map((course, index) => (
                  <div className="col-md-4 mb-4" key={index}>
                    <div 
                      className={`card ${styles.courseCard}`}
                      onClick={() => handleCourseClick(course)}
                    >
                      <img
                        src={course.image ? JSON.parse(course.image)[0] : "https://static.vecteezy.com/system/resources/thumbnails/026/911/382/small_2x/happy-student-boy-with-books-isolated-free-photo.jpg"}
                        alt={course.name}
                        className={`card-img-top ${styles.courseCardImage}`}
                      />
                      <div className={styles.courseCardBody}>
                        <h5 className={styles.courseCardTitle}>{course.name}</h5>
                        <div className="text-center">
                          <span className={`badge ${styles.courseBadge}`}>
                            {t("instructorsPage2.course")}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className={styles.noCourses}>{t("instructorsPage2.noCourses")}</p>
            )}
          </div>

        </div>

      </section>
    </>
  );
}
